import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'

import { INTERAX_COLORS } from '../../../../constants/colors'
import ComparisonBarChartCanvas from './ComparisonBarChartCanvas'
import ComparisonBarChartHeader from './ComparisonBarChartHeader'
import ComparisonBarChartNoData from './ComparisonBarChartNoData'
import { CHART_CANVAS_WIDTH, CHART_HEIGHT } from './comparisonBarChartConstants'
import { calculateChartGeometry } from './comparisonBarChartMath'

export interface ComparisonBarChartVisualizationProps {
  current: number
  previous?: number
  title?: string
  description?: string
  variant?: 'team' | 'individual'
  metricName?: string
}

const CARD_CONTAINER_SX: SxProps<Theme> = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px',
  gap: '24px',
  isolation: 'isolate',
  position: 'relative',
  width: '100%',
  height: '308px',
  background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
  backgroundBlendMode: 'soft-light, normal',
  border: `1px solid ${INTERAX_COLORS.white04}`,
  boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
  borderRadius: '10px',
  overflow: 'hidden'
}

const GLOW_SX: SxProps<Theme> = {
  position: 'absolute',
  width: '244px',
  height: '177px',
  left: '-120px',
  top: '-80px',
  background: INTERAX_COLORS.sun,
  filter: 'blur(75px)',
  zIndex: 0
}

const FALLBACK_IMAGE_SX = {
  width: `${CHART_CANVAS_WIDTH}px`,
  height: `${CHART_HEIGHT}px`,
  objectFit: 'contain' as const,
  zIndex: 1
}

const ComparisonBarChartVisualization: React.FC<ComparisonBarChartVisualizationProps> = ({
  current,
  previous,
  title,
  description: _description,
  variant: _variant,
  metricName
}) => {
  const { t } = useTranslation()

  const isHypothermia =
    metricName?.toLowerCase().includes('paralyzed') || metricName?.toLowerCase().includes('hypothermia')

  const displayTitle =
    title ||
    (isHypothermia
      ? t('gamer.feedback.performance.hypothermiaTitle', 'Hypothermia')
      : t('gamer.feedback.performance.title', 'Performance'))

  const unitLabel = isHypothermia
    ? t('gamer.feedback.performance.hypothermiaUnit', 'seconds')
    : t('gamer.feedback.performance.per15Min', 'per 15 min')
  const mainLabel = isHypothermia
    ? t('gamer.feedback.performance.hypothermiaLabel', 'Spent in Hypothermia')
    : t('gamer.feedback.performance.uridiumCollected', 'Uridium collected')

  const hasNoData = !current && (previous === undefined || previous === null)
  const hasCurrentOnly = current && (previous === undefined || previous === null)

  const [shouldAnimate, setShouldAnimate] = useState(false)

  useEffect(() => {
    if (hasNoData || hasCurrentOnly) {
      setShouldAnimate(false)
      return
    }

    setShouldAnimate(false)

    const timeout = setTimeout(() => {
      setShouldAnimate(true)
    }, 80)

    return () => {
      clearTimeout(timeout)
    }
  }, [hasNoData, hasCurrentOnly, current, previous])

  if (hasNoData) {
    return (
      <ComparisonBarChartNoData
        title={displayTitle}
        message={t('gamer.feedback.ui.noDataAvailable', 'No data available')}
        cardSx={{ ...CARD_CONTAINER_SX, gap: '16px' }}
        glowSx={GLOW_SX}
      />
    )
  }

  if (hasCurrentOnly) {
    return (
      <Box sx={CARD_CONTAINER_SX}>
        <Box sx={GLOW_SX} />
        <ComparisonBarChartHeader
          title={displayTitle}
          mainLabel={mainLabel}
          currentValue={current}
          unitLabel={unitLabel}
        />
        <Box
          component="img"
          src="/assets/img/comparison_bar_chart.png"
          alt={t('gamer.feedback.performance.chartFallbackAlt', 'Performance comparison placeholder')}
          sx={FALLBACK_IMAGE_SX}
        />
      </Box>
    )
  }

  const previousValue = previous as number
  const maxValue = Math.max(current, previousValue, 1)

  const { movementHeights, hasMovementBars, movementLeft, previousCenterX, currentCenterX, trendPoints } =
    calculateChartGeometry({ currentValue: current, previousValue, maxValue })

  const previousSessionLabel = t('gamer.feedback.performance.previousSession', 'Previous session')
  const currentSessionLabel = t('gamer.feedback.performance.thisSession', 'This session')

  return (
    <Box sx={CARD_CONTAINER_SX}>
      <Box sx={GLOW_SX} />
      <ComparisonBarChartHeader
        title={displayTitle}
        mainLabel={mainLabel}
        currentValue={current}
        unitLabel={unitLabel}
      />
      <ComparisonBarChartCanvas
        trendPoints={trendPoints}
        shouldAnimate={shouldAnimate}
        movementHeights={movementHeights}
        movementLeft={movementLeft}
        hasMovementBars={hasMovementBars}
        previousValue={previousValue}
        currentValue={current}
        maxValue={maxValue}
        previousCenterX={previousCenterX}
        currentCenterX={currentCenterX}
        previousLabel={previousSessionLabel}
        currentLabel={currentSessionLabel}
        previousAriaLabel={previousSessionLabel}
        currentAriaLabel={currentSessionLabel}
      />
    </Box>
  )
}

export default ComparisonBarChartVisualization
